// Accordion labels switched to sentence case (acronyms kept).
import React, { FC, useCallback, useEffect, useMemo } from 'react';

import { DataAccessStatus } from '@ariksa/inventory-core';
import { Accordion, Box, Button, Center, Stack, Text } from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { CustomIconTypes } from 'components/Icons/types';
import { MetaAccordionItem } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/MetaAccordionItem';
import { PANEL_HEIGHT } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Identity/Principals/utils';
import { useRedirectToGraph } from 'containers/ActiveCloudResource/hooks/useRedirectToGraph';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';

import { DataAccessToolbar } from './DataAccessToolbar';
import { DataFilterButtons } from './DataFilterButtons';
import { SchemaRows } from './SchemaRows';
import { ROW_CELL_STYLES, useDataAccessColumns } from './useDataAccessColumns';
import {
  DataAccessIdentity,
  useDataAccessSources,
  useDataAccessSummary,
} from './useDataAccessQueries';
import { useDataAccessView, useOnRollupChange } from './useDataAccessView';
import {
  DataAccessRow,
  getErrorMessage,
  getSummaryCounts,
  toDatabaseRow,
} from './utils';

/**
 * The Data tab of a Snowflake user: the databases it can access, one page at
 * a time from the precomputed rollup. Opening a database loads its schemas,
 * and opening a schema one page of its tables, both live. A table opens to
 * its exact privileges, granting roles, owner and tags. The filters apply at
 * every level.
 */
export const SnowflakeUserData: FC = () => {
  const { resourceUuid, resourceAccountId } = useSelector(selectActiveResource);
  const { redirectToGraph } = useRedirectToGraph();
  const identity: DataAccessIdentity = useMemo(
    () => ({ uuid: resourceUuid, accountId: resourceAccountId }),
    [resourceUuid, resourceAccountId],
  );

  const { state: view, actions } = useDataAccessView(resourceUuid);
  const summary = useDataAccessSummary(identity);
  const sources = useDataAccessSources(
    identity,
    view.filters,
    view.databasePage,
  );

  const computedAt = sources.data?.computed_at;
  useOnRollupChange(resourceUuid, computedAt, actions.resetPages);
  // The list was recomputed after the header counts were read.
  const summaryAt = summary.data?.computed_at;
  const refetchSummary = summary.refetch;
  useEffect(() => {
    if (computedAt && summaryAt && computedAt !== summaryAt) refetchSummary();
  }, [computedAt, summaryAt, refetchSummary]);

  const onToggle = useCallback(
    (row: DataAccessRow) => {
      if (row.kind === 'database') actions.toggleDatabase(row.uuid);
      else if (row.kind === 'schema') actions.toggleSchema(row.uuid);
      else actions.toggleTable(row.uuid);
    },
    [actions],
  );
  const columns = useDataAccessColumns(onToggle);

  const rows = useMemo(
    () =>
      (sources.data?.items ?? []).map(source =>
        toDatabaseRow(source, view.openDatabases.includes(source.uuid)),
      ),
    [sources.data, view.openDatabases],
  );
  const counts = getSummaryCounts(summary.data, view.filters);
  const notComputed =
    summary.data?.status === DataAccessStatus.NotComputed ||
    sources.data?.status === DataAccessStatus.NotComputed;

  const renderTable = () => {
    if (notComputed)
      return (
        <Center h="full" color="gray.400" textAlign="center">
          Data access for this account hasn't been computed yet. It is computed
          after each collection.
        </Center>
      );
    if (sources.isError && !sources.data)
      return (
        <Center h="full">
          <Stack align="center" spacing={3}>
            <Text color="critical">
              {getErrorMessage(sources.error, 'Failed to load databases')}
            </Text>
            <Button
              size="sm"
              variant="outline"
              onClick={() => sources.refetch()}
            >
              Retry
            </Button>
          </Stack>
        </Center>
      );
    return (
      <Table
        columns={columns}
        data={rows}
        isLoading={sources.isLoading}
        noDataMessage="No databases match these filters"
        expanded
        // An open database shows its schemas under it.
        subComponent={({ row }) =>
          row.isOpen ? (
            <SchemaRows
              identity={identity}
              database={row}
              view={view}
              actions={actions}
              columns={columns}
            />
          ) : null
        }
        onRowClick={row => row.canOpen && actions.toggleDatabase(row.uuid)}
        cursor={row => (row.canOpen ? 'pointer' : 'default')}
        pagination={{
          totalCount: sources.data?.total ?? 0,
          pageInfo: view.databasePage,
          onChange: actions.setDatabasePage,
        }}
        styles={{
          cell: ROW_CELL_STYLES,
          // No active-row tint: open rows are marked by their chevron.
          row: {
            bg: 'white',
            opacity: sources.isPlaceholderData ? 0.5 : 1,
            css: { 'td:first-of-type': { background: 'white' } },
          },
        }}
      />
    );
  };

  const panel = (
    <Stack h="full" w="full" spacing={3}>
      <DataAccessToolbar
        filters={view.filters}
        counts={counts}
        computedAt={summaryAt}
        onChange={actions.setFilters}
      />
      <Box flex={1} minH={0}>
        {renderTable()}
      </Box>
    </Stack>
  );

  return (
    <Accordion defaultIndex={[0]} allowMultiple>
      <MetaAccordionItem
        iconType={CustomIconTypes.DataScan}
        label="Authorized Data Sources"
        totalCount={counts.databases}
        isLoading={summary.isLoading}
        rightHeader={() =>
          summary.data && !notComputed ? (
            <DataFilterButtons
              summary={summary.data}
              filters={view.filters}
              onChange={actions.setFilters}
            />
          ) : null
        }
        panel={panel}
        height={PANEL_HEIGHT}
        styles={{ panel: { pt: '6px', pb: 2 } }}
        iconBGColor="primary"
        onShowGraph={() => redirectToGraph('access', MetadataTabs.Data)}
      />
    </Accordion>
  );
};
